/**
 * Tiny typed wrapper over localStorage for the web scanner. Same shape as
 * apps/scanner/lib/storage.ts so the offline-validation logic can be kept
 * in sync between the two implementations.
 *
 * We intentionally don't use IndexedDB for now — the bundle for a single
 * event is hundreds of KB at most (one row per QR + ticketSnapshot), well
 * within localStorage's 5MB quota.
 */

const PREFIX = 'boletra:scanner:';

export const storage = {
  get<T>(key: string): T | null {
    try {
      const raw = window.localStorage.getItem(`${PREFIX}${key}`);
      if (!raw) return null;
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  },
  set<T>(key: string, value: T): void {
    try {
      window.localStorage.setItem(`${PREFIX}${key}`, JSON.stringify(value));
    } catch {
      // Quota exceeded or private browsing — fail silently. Worst case the
      // page reloads and we re-pull the bundle from the server.
    }
  },
  remove(key: string): void {
    window.localStorage.removeItem(`${PREFIX}${key}`);
  },
};

export const KEYS = {
  scannerToken: (eventId: string) => `scanner-token:${eventId}`,
  syncBundle: (eventId: string) => `sync-bundle:${eventId}`,
  pendingValidations: (eventId: string) => `pending-validations:${eventId}`,
  validatedQrs: (eventId: string) => `validated:${eventId}`,
  scanHistory: (eventId: string) => `scan-history:${eventId}`,
  sessionStats: (eventId: string) => `session-stats:${eventId}`,
  deviceId: 'device-id',
} as const;

export function ensureDeviceId(): string {
  const existing = storage.get<string>(KEYS.deviceId);
  if (existing) return existing;
  const id = `web_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`;
  storage.set(KEYS.deviceId, id);
  return id;
}
